import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {EntityType} from 'nutshell-graphql-types';
import type {
    UnarchiveCustomField as UnarchiveCustomFieldMutation,
    UnarchiveCustomFieldVariables as UnarchiveCustomFieldVariables,
    CustomFieldUndeleteInputs,
} from 'nutshell-graphql-types';

import UNARCHIVE_CUSTOM_FIELD from '../mutations/unarchive-custom-field.graphql';
import GET_CUSTOM_FIELD_STAGESETS from '../queries/get-custom-field-stagesets.graphql';
import GET_CUSTOM_FIELDS from '../queries/get-custom-fields.graphql';

export const useUnarchiveCustomField = (): {
    unarchiveCustomField: (arg1: CustomFieldUndeleteInputs, entityType: EntityType) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [unarchiveCustomField, {loading, error}] = useMutation<
        UnarchiveCustomFieldMutation,
        UnarchiveCustomFieldVariables
    >(UNARCHIVE_CUSTOM_FIELD);

    return {
        unarchiveCustomField: (input: CustomFieldUndeleteInputs, entityType: EntityType) => {
            return unarchiveCustomField({
                variables: {input},
                update: (proxy, {data}) => {
                    const restoredCustomField =
                        data && data.undeleteCustomField && data.undeleteCustomField.customField;

                    if (!restoredCustomField) return;

                    const customFields = proxy.readQuery({
                        query: GET_CUSTOM_FIELDS,
                        variables: {input: {entityType}},
                    });

                    if (!customFields) return;

                    proxy.writeQuery({
                        query: GET_CUSTOM_FIELDS,
                        variables: {input: {entityType}},
                        data: {
                            customFields: {
                                // @ts-expect-error
                                ...customFields.customFields,
                                // @ts-expect-error
                                archived: customFields.customFields.archived.filter(
                                    // @ts-expect-error
                                    (field) => field.id !== restoredCustomField.id
                                ),
                                // @ts-expect-error
                                active: [restoredCustomField, ...customFields.customFields.active],
                            },
                        },
                    });

                    const stagesetCustomFields = proxy.readQuery({
                        query: GET_CUSTOM_FIELD_STAGESETS,
                    });

                    if (!stagesetCustomFields) return;

                    proxy.writeQuery({
                        query: GET_CUSTOM_FIELD_STAGESETS,
                        data: {
                            // @ts-expect-error
                            stagesets: stagesetCustomFields.stagesets.map((stageset) => ({
                                ...stageset,
                                customFields: {
                                    ...stageset.customFields,
                                    shown: [...stageset.customFields.shown, restoredCustomField],
                                    hidden: stageset.customFields.hidden.filter(
                                        // @ts-expect-error
                                        (field) => field.id !== restoredCustomField.id
                                    ),
                                },
                            })),
                        },
                    });
                },
            });
        },
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};
